// src/pages/admin/tables/Tables.jsx
import { useState, useEffect } from 'react'
import { 
  Plus, 
  Search, 
  Filter, 
  MoreHorizontal, 
  Edit, 
  Trash2,
  RefreshCw,
  Hash,
  MapPin,
  Users,
  Clock,
  CheckCircle,
  XCircle,
  Eye,
  ChevronDown
} from 'lucide-react'
import { Button } from '../../../components/ui/Button'
import { Input } from '../../../components/ui/Input'
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card'
import TableService from '../../../services/tableService'
import BranchService from '../../../services/branchService'
import AuthService from '../../../services/authService'
import { usePermissions } from '../../../hooks/usePermissions'
import AddTableModal from '../../../components/modals/AddTableModal'
import EditTableModal from '../../../components/modals/EditTableModal'
import DeleteTableModal from '../../../components/modals/DeleteTableModal'
import ViewTableModal from '../../../components/modals/ViewTableModal'
import { TableModel, TABLE_AVAILABILITY, AVAILABILITY_CONFIG } from '../../../utils/tableModel'
import CardLoader from '../../../components/ui/CardLoader'

// Toast Component
const Toast = ({ message, type, onClose }) => (
  <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg flex items-center space-x-2 ${
    type === 'success' ? 'bg-green-500 text-white' : 
    type === 'error' ? 'bg-red-500 text-white' : 'bg-blue-500 text-white'
  }`}>
    {type === 'success' ? <CheckCircle className="h-5 w-5" /> : 
     type === 'error' ? <XCircle className="h-5 w-5" /> : <Hash className="h-5 w-5" />}
    <span>{message}</span>
    <button onClick={onClose} className="ml-2">
      <XCircle className="h-4 w-4" />
    </button>
  </div>
)

export const Tables = () => {
  // Get current user and permissions
  const currentUser = AuthService.getCurrentUser()
  const permissions = usePermissions(currentUser)

  // State management
  const [tables, setTables] = useState([])
  const [branches, setBranches] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [branchFilter, setBranchFilter] = useState('all')
  const [availabilityFilter, setAvailabilityFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')
  const [sortBy, setSortBy] = useState('number')
  const [sortOrder, setSortOrder] = useState('asc')
  const [toast, setToast] = useState(null)

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [selectedTable, setSelectedTable] = useState(null)

  // Pagination
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(10)

  // Safe getter utility
  const safeGet = (obj, path, defaultValue = null) => {
    try {
      return path.split('.').reduce((current, key) => {
        return current && current[key] !== undefined ? current[key] : defaultValue
      }, obj)
    } catch {
      return defaultValue
    }
  }

  // Helper function to validate table object
  const validateTable = (table) => {
    if (!table || typeof table !== 'object') return false
    return table._id && table.number && table.branch
  }

  // Show toast notification
  const showToast = (message, type = 'success') => {
    setToast({ message, type })
    setTimeout(() => setToast(null), 3000)
  }

  // Initialize filters for branch-restricted users
  useEffect(() => {
    if (permissions.isBranchRestricted && currentUser?.branchId) {
      console.log('🎯 [TABLES] Auto-setting branch filter for manager:', {
        branchId: currentUser.branchId,
        userRole: currentUser?.role
      })
      setBranchFilter(currentUser.branchId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissions.isBranchRestricted, currentUser?.branchId])

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('')
    setBranchFilter(permissions.isBranchRestricted && currentUser?.branchId ? 
                    currentUser.branchId : 'all')
    setAvailabilityFilter('all')
    setStatusFilter('all')
    setSortBy('number')
    setSortOrder('asc')
    setCurrentPage(1)
  }

  // Initial data fetch - using pattern to prevent infinite reloads
  useEffect(() => {
    // Fetch tables with branch filtering
    const loadTables = async () => {
      try {
        setLoading(true)
        setError(null)
        
        // Log branch access for security
        permissions.logBranchAccess('tables', currentUser?.branchId)

        // Apply branch filtering for restricted users (managers)
        let tableParams = { page: 1, limit: 100 }
        if (permissions.isBranchRestricted && currentUser?.branchId) {
          tableParams.branchId = currentUser.branchId
          console.log(`🏢 [MANAGER FILTER] Loading tables for branch: ${currentUser.branchId}`)
        }
        
        const response = await TableService.getAllTables(tableParams)
        
        if (response && response.success) {
          const tablesData = safeGet(response, 'data.tables', []) || 
                            safeGet(response, 'data.docs', []) || 
                            safeGet(response, 'data', [])
          
          console.log('🪑 Tables response structure:', response.data)
          console.log('🪑 Extracted tables data:', tablesData)
          
          // Log sample table structure
          if (tablesData.length > 0) {
            console.log('🪑 Sample table structure:', tablesData[0])
          }
          
          // Filter out invalid tables
          const validTables = Array.isArray(tablesData) 
            ? tablesData.filter(validateTable)
            : []
          
          console.log('🪑 Valid tables loaded:', validTables.length)
          if (permissions.isBranchRestricted) {
            console.log('🔒 Branch-restricted user - tables loaded for specific branch')
          }
          setTables(validTables)
        } else {
          console.warn('⚠️ Unexpected response format:', response)
          setError('Failed to load tables')
          setTables([])
        }
      } catch (error) {
        console.error('❌ Error fetching tables:', error)
        setError(error.message || 'Failed to load tables')
        setTables([])
      } finally {
        setLoading(false)
      }
    }

    // Fetch branches for filter dropdown (admin only)
    const loadBranches = async () => {
      if (!permissions.canAccessAllBranches) {
        console.log('🔒 Branch-restricted user - skipping branches fetch')
        return
      }

      try {
        console.log('🏢 Fetching branches for filter...')
        const response = await BranchService.getAllBranches()
        
        if (response && response.success) {
          const branchesData = response.data?.branches || []
          console.log('🏢 Branches fetched for filter:', branchesData.length)
          
          // Filter only active branches
          const activeBranches = branchesData.filter(branch => branch.isActive)
          setBranches(activeBranches)
        } else {
          console.warn('⚠️ Failed to fetch branches:', response)
          setBranches([])
        }
      } catch (error) {
        console.error('❌ Error fetching branches:', error)
        setBranches([])
      }
    }

    // Initial data load
    loadTables()
    loadBranches()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Empty dependency array to prevent infinite loops

  // Get branch name helper
  const getBranchName = (table) => {
    if (!table.branch) return 'Unknown'
    
    // If branch is populated object
    if (typeof table.branch === 'object' && table.branch.name) {
      return table.branch.name
    }
    
    // If we have branch details
    if (table.branchDetails && table.branchDetails.name) {
      return table.branchDetails.name
    }
    
    // Find branch by ID
    const foundBranch = branches.find(branch => branch._id === table.branch)
    if (foundBranch) {
      return foundBranch.name
    }
    
    return 'Unknown Branch'
  }

  // Filter and search tables
  const getFilteredTables = () => {
    let filtered = [...tables]

    // Search filter
    if (searchTerm) {
      filtered = TableModel.searchTables(filtered, searchTerm)
    }

    // Branch filter
    if (branchFilter !== 'all') {
      filtered = TableModel.filterByBranch(filtered, branchFilter)
    }

    // Availability filter
    if (availabilityFilter !== 'all') {
      filtered = TableModel.filterByAvailability(filtered, availabilityFilter)
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = TableModel.filterByStatus(filtered, statusFilter)
    }

    // Sort
    filtered = TableModel.sortTables(filtered, sortBy, sortOrder)

    return filtered
  }

  // Get paginated tables
  const getPaginatedTables = () => {
    const filtered = getFilteredTables()
    const startIndex = (currentPage - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    return filtered.slice(startIndex, endIndex)
  }

  // Get pagination info
  const getPaginationInfo = () => {
    const total = getFilteredTables().length
    const totalPages = Math.ceil(total / itemsPerPage)
    return { total, totalPages }
  }

  // Handle table added
  const handleTableAdded = (newTable) => {
    if (validateTable(newTable)) {
      setTables(prev => [newTable, ...prev])
      showToast('Table created successfully')
      // Reset to first page to show the new table
      setCurrentPage(1)
    }
  }

  // Handle table updated
  const handleTableUpdated = (updatedTable) => {
    if (validateTable(updatedTable)) {
      setTables(prev => 
        prev.map(table => 
          table._id === updatedTable._id ? updatedTable : table
        )
      )
      showToast('Table updated successfully')
    }
  }

  // Handle table deleted
  const handleTableDeleted = (deletedTableId) => {
    setTables(prev => prev.filter(table => table._id !== deletedTableId))
    showToast('Table deleted successfully')
    
    // Adjust current page if needed
    const newTotal = tables.length - 1
    const newTotalPages = Math.ceil(newTotal / itemsPerPage)
    if (currentPage > newTotalPages && newTotalPages > 0) {
      setCurrentPage(newTotalPages)
    }
  }

  // Handle edit table
  const handleEditTable = (table) => {
    setSelectedTable(table)
    setShowEditModal(true)
  }

  // Handle delete table
  const handleDeleteTable = (table) => {
    setSelectedTable(table)
    setShowDeleteModal(true)
  }

  // Handle view table
  const handleViewTable = (table) => {
    setSelectedTable(table)
    setShowViewModal(true)
  }

  // Handle refresh
  const handleRefresh = () => {
    // Create a manual refresh function
    const refreshData = async () => {
      try {
        setLoading(true)
        setError(null)
        
        // Log branch access for security
        permissions.logBranchAccess('tables', currentUser?.branchId)

        // Apply branch filtering for restricted users (managers)
        let tableParams = { page: 1, limit: 100 }
        if (permissions.isBranchRestricted && currentUser?.branchId) {
          tableParams.branchId = currentUser.branchId
          console.log(`🔄 [REFRESH] Loading tables for branch: ${currentUser.branchId}`)
        }
        
        const response = await TableService.getAllTables(tableParams)
        
        if (response && response.success) {
          const tablesData = safeGet(response, 'data.tables', []) || 
                            safeGet(response, 'data.docs', []) || 
                            safeGet(response, 'data', [])
          
          const validTables = Array.isArray(tablesData) 
            ? tablesData.filter(validateTable)
            : []
          
          console.log('🔄 [REFRESH] Tables refreshed:', validTables.length)
          setTables(validTables)
        } else {
          setError('Failed to load tables')
          setTables([])
        }
      } catch (error) {
        console.error('❌ Error refreshing tables:', error)
        setError(error.message || 'Failed to load tables')
        setTables([])
      } finally {
        setLoading(false)
      }
    }

    refreshData()
    showToast('Tables refreshed')
  }

  // Get table summary
  const getTablesSummary = () => {
    return TableModel.getTablesSummary(tables)
  }

  const summary = getTablesSummary()
  const { total, totalPages } = getPaginationInfo()
  const paginatedTables = getPaginatedTables()
  const filteredData = getFilteredTables()

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Table Management</h1>
        </div>
        <div className="flex items-center space-x-3">
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={loading}
            className="flex items-center"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          
          <Button
            onClick={() => setShowAddModal(true)}
            className="bg-orange-600 hover:bg-orange-700 text-white"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Table
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="bg-white">
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-3 bg-orange-100 rounded-lg">
                <Hash className="h-8 w-8 text-orange-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Tables</p>
                <p className="text-2xl font-bold text-gray-900">{summary.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white">
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-3 bg-green-100 rounded-lg">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Available</p>
                <p className="text-2xl font-bold text-gray-900">{summary.available}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white">
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-3 bg-red-100 rounded-lg">
                <Clock className="h-8 w-8 text-red-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Reserved</p>
                <p className="text-2xl font-bold text-gray-900">{summary.reserved}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white">
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Users className="h-8 w-8 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Capacity</p>
                <p className="text-2xl font-bold text-gray-900">{summary.totalCapacity}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Search */}
      <Card className="mb-6">
        <CardContent className="p-6">
          {/* Filter Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <Filter className="h-5 w-5 text-gray-600" />
              <h2 className="text-lg font-semibold text-gray-900">Filter Tables</h2>
            </div>
            {(searchTerm || branchFilter !== 'all' || availabilityFilter !== 'all' || statusFilter !== 'all') && (
              <Button
                variant="outline"
                onClick={clearFilters}
                className="text-orange-500 border-orange-500 hover:bg-orange-50"
              >
                Clear Filters
              </Button>
            )}
          </div>

          {/* Filter Controls */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            {/* Search */}
            <div className="relative">
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2 z-10">
                <Search className="h-4 w-4 text-gray-400" />
              </div>
              <Input
                type="text"
                placeholder="Search table number..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value)
                  setCurrentPage(1)
                }}
                className="pl-10 h-11 border-gray-300"
              />
            </div>

            {/* Branch Filter */}
            <div className="relative">
              <select
                value={branchFilter}
                onChange={(e) => {
                  setBranchFilter(e.target.value)
                  setCurrentPage(1)
                }}
                disabled={permissions.isBranchRestricted}
                className={`w-full h-11 px-3 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 appearance-none bg-white ${
                  permissions.isBranchRestricted ? 'bg-gray-100 cursor-not-allowed' : ''
                }`}
              >
                <option value="all">All Branches</option>
                {branches.map(branch => (
                  <option key={branch._id} value={branch._id}>
                    {branch.name}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            </div>

            {/* Availability Filter */}
            <div className="relative">
              <select
                value={availabilityFilter}
                onChange={(e) => {
                  setAvailabilityFilter(e.target.value)
                  setCurrentPage(1)
                }}
                className="w-full h-11 px-3 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 appearance-none bg-white"
              >
                <option value="all">All Availability</option>
                {Object.values(TABLE_AVAILABILITY).map(availability => (
                  <option key={availability} value={availability}>
                    {AVAILABILITY_CONFIG[availability].label}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            </div>

            {/* Status Filter */}
            <div className="relative">
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value)
                  setCurrentPage(1)
                }}
                className="w-full h-11 px-3 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 appearance-none bg-white"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* Results Count */}
          <div className="text-sm text-gray-600">
            Showing {filteredData.length} of {tables.length} tables
          </div>
        </CardContent>
      </Card>

      {/* Tables Table */}
      {loading ? (
        <CardLoader title="Loading tables..." />
      ) : error ? (
        <Card className="bg-white">
          <CardContent className="py-12">
            <div className="text-center">
              <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Data</h3>
              <p className="text-gray-600 mb-4">{error}</p>
              <Button onClick={handleRefresh} leftIcon={RefreshCw}>
                Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-white">
          <CardContent className="p-0">
            {filteredData.length === 0 ? (
              <div className="text-center py-12">
                <Hash className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {tables.length === 0 ? 'No tables found' : 'No tables match your filters'}
                </h3>
                <p className="text-gray-500 mb-4">
                  {tables.length === 0
                    ? 'Create your first table to get started.'
                    : 'Try adjusting your search criteria.'
                  }
                </p>
                {tables.length === 0 && (
                  <Button
                    onClick={() => setShowAddModal(true)}
                    className="bg-orange-500 hover:bg-orange-600 text-white"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Create First Table
                  </Button>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        TABLE NUMBER
                      </th>
                      <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        CAPACITY
                      </th>
                      <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        AVAILABILITY
                      </th>
                      <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        BRANCH
                      </th>
                      <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        STATUS
                      </th>
                      <th scope="col" className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        ACTIONS
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {paginatedTables.map((table) => (
                      <tr key={table._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <Hash className="h-4 w-4 text-gray-400 mr-2" />
                            <span className="text-sm font-medium text-gray-900">{table.number}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <Users className="h-4 w-4 text-gray-400 mr-2" />
                            <span className="text-sm text-gray-900">{table.capacity}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            TableModel.getAvailabilityBadgeClass(table.availability)
                          }`}>
                            {TableModel.getAvailabilityIcon(table.availability)} {
                              AVAILABILITY_CONFIG[table.availability]?.label || table.availability
                            }
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center text-sm text-gray-900">
                            <MapPin className="h-4 w-4 mr-2 text-gray-400" />
                            {getBranchName(table)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            TableModel.getStatusBadgeClass(table.isActive)
                          }`}>
                            {table.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <div className="flex items-center justify-end space-x-2">
                            <button
                              onClick={() => handleViewTable(table)}
                              className="text-gray-400 hover:text-gray-600 p-1 rounded"
                              title="View Details"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleEditTable(table)}
                              className="text-gray-400 hover:text-gray-600 p-1 rounded"
                              title="Edit Table"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteTable(table)}
                              className="text-gray-400 hover:text-red-600 p-1 rounded"
                              title="Delete Table"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Pagination */}
      {!loading && !error && totalPages > 1 && (
        <div className="flex items-center justify-between mt-6 pt-6 border-t border-gray-200">
          <div className="text-sm text-gray-700">
            Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, total)} of {total} results
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              Previous
            </Button>
            <span className="text-sm text-gray-700">
              Page {currentPage} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Modals */}
      <AddTableModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onTableAdded={handleTableAdded}
        restrictedBranchId={permissions.isBranchRestricted ? currentUser?.branchId : null}
        isBranchRestricted={permissions.isBranchRestricted}
      />

      <ViewTableModal
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        onEdit={handleEditTable}
        table={selectedTable}
      />

      <EditTableModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onTableUpdated={handleTableUpdated}
        table={selectedTable}
        restrictedBranchId={permissions.isBranchRestricted ? currentUser?.branchId : null}
        isBranchRestricted={permissions.isBranchRestricted}
      />

      <DeleteTableModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onTableDeleted={handleTableDeleted}
        table={selectedTable}
      />
    </div>
  )
}